
/**
 * ParticipantsTableNew Component
 *
 * Displays a sortable, filterable table of course participants (students/deltakere).
 * Features:
 * - Drag-and-drop ranking for participants
 * - Search and filter capabilities
 * - Conditional column display based on course type
 * - Formatted data display (status, dates, percentages)
 * - Access control based on user permissions
 *
 * @component
 */
import { computed, defineComponent, onMounted, ref, watch } from "@vue/composition-api";
import { mapGetters } from "vuex";
import { VSwitch } from "vuetify/lib";
import { SortableEvent } from "sortablejs";
import isBefore from "date-fns/isBefore";

// api
import { api } from "@/api/api";
import {
  ApiGetCourseParticipantKursAdminDto,
  ApiIdRankDto,
  ApiGetGuestEmployeewithMemberOrganizationDto,
} from "@/api/generated/Api";

// enums
import { CourseStatus } from "@/shared/enums/CourseStatus.enum";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { ModalType } from "@/shared/enums/modalTypeEnum";

// helpers
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { hasMemberOrgAccess } from "@/shared/helpers/accessLevelApiAdapter";
import { isVocationalSchool } from "@/shared/helpers/curriculumHelpers";
import { formatDateShort, formatDateTime, formatRelative } from "@/shared/helpers/dateHelpers";
import { openNotification } from "@/shared/helpers/store.helpers";
import { useRoute, useStore } from "@/shared/useHelpers";

// components
import BaseTableFiltered from "@/components/shared/table/BaseTableFiltered.vue";
import BaseTooltipIconButton from "@/components/shared/button/BaseTooltipIconButton.vue";
import BaseModal from "@/components/shared/BaseModal.vue";
import BaseConfirmModalForm from "@/components/shared/modal/BaseConfirmModalForm.vue";
import UpsertParticipantModal from "./UpsertParticipantModalNew2.vue";

import { getInitialModalData, useOpenModal } from "@/fragments/modal/useOpenModal";
import { StoreState } from "@/store/store.state.interface";

export default defineComponent({
  name: "ParticipantsTableNew",
  components: {
    BaseTableFiltered,
    BaseTooltipIconButton,
    BaseModal,
    BaseConfirmModalForm,
    UpsertParticipantModal,
  },
  props: {
    course: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const store = useStore<StoreState>();
    const route = useRoute();

    // State management
    const courseParticipants = ref<ApiGetCourseParticipantKursAdminDto[]>([]);
    const loading = ref(false);
    const showModal = ref(false);
    // Sequence counter to discard results from superseded fetches (race condition guard).
    let fetchSequence = 0;
    const canEditParticpantCheck = ref<boolean>(false);
    const userBypassPermission = ref<boolean>(false);
    const deleteParticipantModalData = ref(getInitialModalData());
    const featureSettings = ref<any>(null);
    const currentGuestUser = ref<ApiGetGuestEmployeewithMemberOrganizationDto>();
    const selectedCourseId = computed(() => Number(route.params.id) || props.course?.id || 0);

    /**
     * Fetches course participants from the API
     * Uses the new active-students endpoint that filters for active students only
     * Also extracts feature settings for permission checks
     * Note: This endpoint is available for both employees and guests
     */
    const getCourseParticipants = async () => {
      if (!selectedCourseId.value) {
        return;
      }

      const currentFetch = ++fetchSequence;
      loading.value = true;
      try {
        const response = await api.course.getActiveStudentsOnCourseByCourseIdAsync(selectedCourseId.value);

        // Discard if a newer fetch has already been initiated.
        if (currentFetch !== fetchSequence) {
          return;
        }

        // New API returns { course: { featureSettings: {...} }, students: [...] }
        // Type assertion needed until API types are regenerated
        const data = response.data as any;
        courseParticipants.value = data.students || [];
        featureSettings.value = data.course?.featureSettings || null;
      } catch (error) {
        console.error("Error fetching participants:", error);
      } finally {
        // Only clear the loading spinner if this is still the active fetch.
        if (currentFetch === fetchSequence) {
          loading.value = false;
        }
      }
    };

    /**
     * Check if user is a part of subOrganizer or accounting department
     * If so they can change the status from preapproved to approved before deadline
     */
    const getCurrentUser = async () => {
      if (!hasMemberOrgAccess) {
        return;
      }

      let topLevelId = 0;
      currentGuestUser.value = (await api.guestside.getGuestUserCurrentGuestEmployeeAsync()).data;
      const memberOrganizations = (await api.guestside.getGuestUserMemberOrganizations()).data;
      let isRoot = false;

      if (memberOrganizations && memberOrganizations?.length > 0) {
        const myOrg = memberOrganizations.find((x: any) => x.id === currentGuestUser.value?.memberOrganization?.id);
        if (myOrg?.parentId === undefined) {
          isRoot = true;
          topLevelId = myOrg?.id || 0;
        } else {
          const isMyOrgParent: boolean = memberOrganizations.some((x: any) => x.parentId == myOrg.id);
          if (isMyOrgParent) {
            isRoot = false;
            topLevelId = myOrg.parentId !== null ? myOrg.parentId : 0;
          } else {
            isRoot = false;
            const parentOrg = memberOrganizations.find((x: any) => x.id === myOrg.parentId);
            topLevelId =
              parentOrg?.parentId !== null && parentOrg?.parentId !== undefined
                ? parentOrg.parentId
                : myOrg.parentId !== null
                ? myOrg.parentId
                : 0;
          }
        }
      } else {
        topLevelId = currentGuestUser.value?.memberOrganization?.id || 0;
      }

      checkGuestUserTopLevelMemberOrganizationFeaturesSettings(topLevelId, isRoot);
    };

    /**
     * Check if guest user's organization allows pre-approval
     */
    const checkGuestUserTopLevelMemberOrganizationFeaturesSettings = async (topLevelId: number, isRoot: boolean) => {
      if (!hasMemberOrgAccess) {
        return;
      }
      if (!topLevelId) {
        return;
      }

      // Check if user is from accounting department - they can pre-approve for all courses
      if (featureSettings.value?.isAccountingDepartment) {
        userBypassPermission.value = true;
        return;
      }

      // Check if course has bypass enrollment deadline enabled – applies to all guest users
      // (consistent with UpsertParticipantModalNew which checks the same flag without org restriction)
      if (featureSettings.value?.bypassEnrollmentDeadlineInGuestPortal) {
        userBypassPermission.value = true;
        return;
      }

      // Check if user is from root org or organizer org
      if (isRoot || topLevelId === props.course.organizerOrganizationId) {
        if (checkFeatureSettings(topLevelId)) {
          userBypassPermission.value = true;
        }
      }
    };

    /**
     * Check if feature settings allow bypassing enrollment deadline
     */
    const checkFeatureSettings = (id: number) => {
      if (!id || !featureSettings.value) {
        return false;
      }

      // Check if this organization has bypass permission
      if (featureSettings.value.organizationId === id && featureSettings.value.bypassEnrollmentDeadlineInGuestPortal) {
        return true;
      }

      return false;
    };

    /**
     * Handles drag-and-drop reordering of participants in the table
     * Updates local array to reflect new order
     * @param event - SortableJS event containing old and new indices
     */
    const sorted = (event: SortableEvent) => {
      const { newIndex, oldIndex } = event;
      if (newIndex === undefined || oldIndex === undefined) {
        return;
      }
      const element = courseParticipants.value[oldIndex];
      courseParticipants.value.splice(oldIndex, 1);
      courseParticipants.value.splice(newIndex, 0, element);
    };

    /**
     * Returns the display rank for each participant
     * Uses the rank from API if available, otherwise falls back to array index
     * Used to show ranking in the table
     */
    const ranking = computed(() => (item: ApiGetCourseParticipantKursAdminDto) => {
      // If rank is explicitly set (not null/undefined), use it
      if (item.rank !== null && item.rank !== undefined) {
        return item.rank + 1;
      }
      // Otherwise fall back to array position
      return courseParticipants.value.indexOf(item) + 1;
    });

    /**
     * Persists participant ranking to the database
     * Maps current array order to rank values and sends to API
     * @param participants - Array of participants in their current order
     */
    const updateParticipantRank = async (participants: ApiGetCourseParticipantKursAdminDto[]) => {
      if (!selectedCourseId.value) {
        return;
      }

      const participantsRanked: ApiIdRankDto[] = participants.map((student, index) => ({
        userId: student.userId,
        rank: index,
      }));
      await api.course.updateCourseParticipantRanksAsync(selectedCourseId.value, participantsRanked);
      openNotification(store, NotificationItemType.Success, "Rangering oppdatert");
    };

    /**
     * Closes the modal dialog
     */
    const closeModal = () => {
      showModal.value = false;
    };

    /**
     * Opens confirmation modal for deleting a participant
     */
    const openDeleteParticipant = useOpenModal(
      ModalType.Delete,
      isVocationalSchool(store.state.plans.studyplan.mainCourseId) ? "student" : "deltaker",
      deleteParticipantModalData
    );

    /**
     * Deletes a participant from the course
     * Only allowed if participant status is 'avmeldt' (unregistered)
     * @param item - Participant to delete
     */
    const deleteParticipant = async (item: ApiGetCourseParticipantKursAdminDto) => {
      if (!selectedCourseId.value) {
        return;
      }

      globalLoadingWrapper({ blocking: true }, async () => {
        try {
          await api.course.deleteCourseParticipantByCourseIdAsync(selectedCourseId.value, item.userId);
          openNotification(store, NotificationItemType.Success, "Fjerning fullført");
        } catch (e: any) {
          e.response.data.fieldsErrors?.forEach((error: any) => {
            openNotification(store, NotificationItemType.Error, `Valideringsfeil: ${error.error}`);
          });
        } finally {
          await getCourseParticipants();
          deleteParticipantModalData.value.showModal = false;
        }
      });
    };

    /**
     * Computes the tooltip text for the edit participant button
     * Returns different messages based on user permissions and enrollment deadline
     */
    const editParticipantTooltip = computed(() => {
      const baseLabel = isVocationalSchool(store.state.plans.studyplan.mainCourseId)
        ? "Rediger student"
        : "Rediger deltaker";

      // Guest users without bypass permission see deadline restriction message
      if (hasMemberOrgAccess && !userBypassPermission.value) {
        const enrollmentDeadline = new Date(props.course.enrollmentDeadline);
        const now = new Date();
        if (enrollmentDeadline > now) {
          return "Kan ikke redigere deltaker før påmeldingsfrist er gått ut";
        }
      }

      return baseLabel;
    });

    /**
     * Determines if user can edit participants
     * Admin users can always edit, guest users depend on deadline and permissions
     * Guest users with bypassEnrollmentDeadline or isAccountingDepartment can edit before deadline
     */
    const canEditParticipant = () => {
      if (!hasMemberOrgAccess) {
        canEditParticpantCheck.value = true;
        return;
      }

      if (hasMemberOrgAccess && userBypassPermission.value) {
        canEditParticpantCheck.value = true;
        return;
      }

      if (isBefore(new Date(props.course.enrollmentDeadline), new Date())) {
        canEditParticpantCheck.value = true;
      }
    };

    /**
     * Centralized column formatters for consistent data display
     * Each formatter transforms raw data into user-friendly format
     */
    const formatters = computed(() => ({
      status: (value: string) => value.charAt(0).toUpperCase() + value.slice(1),
      userTypeType: (value: string) => (value === "b2c" ? "Minside" : "Begrenset"),
      appliedViaForm: (value: boolean) => (value ? "Søkt gjennom web" : "Lagt til av kursadmin"),
      requiresElectronicInvoice: (value: boolean) => (value ? "Ja" : "Nei"),
      currentAttendancePercentage: (value: number | null) => (value ? `${value}%` : "-"),
      memberOrganization: (org?: any) => {
        if (!org || !org.name) {
          return "Ingen medlemsorganisasjon";
        }
        return org.name;
      },
      sector: (sector?: any) => {
        if (!sector || !sector.type) {
          return "Ingen sektor";
        }
        return sector.type;
      },
    }));

    // Initialize component by fetching participant data and permissions
    onMounted(async () => {
      await getCourseParticipants(); // Must load first to get featureSettings
      await getCurrentUser(); // Check user permissions
      canEditParticipant();
    });

    watch(
      selectedCourseId,
      async (newId, oldId) => {
        if (!newId || newId === oldId) {
          return;
        }

        canEditParticpantCheck.value = false;
        userBypassPermission.value = false;
        await getCourseParticipants();
        await getCurrentUser();
        canEditParticipant();
      },
      { flush: "post" }
    );

    return {
      courseParticipants,
      loading,
      isVocationalSchool: computed(() => isVocationalSchool(store.state.plans.studyplan.mainCourseId)),
      formatDateShort,
      sorted,
      ranking,
      updateParticipantRank,
      isCourseClosed: computed(() => props.course.status === CourseStatus.Closed),
      hasMemberOrgAccess,
      showModal,
      closeModal,
      formatDateTime,
      formatters,
      editParticipantTooltip,
      canEditParticpantCheck,
      deleteParticipant,
      openDeleteParticipant,
      deleteParticipantModalData,
      getCourseParticipants,
      courseId: selectedCourseId,
      featureSettings,
    };
  },
  data() {
    return {
      search: "",
      modalHeadline: "",
      modalType: ModalType.Edit as ModalType,
      selectedUserId: 0,
      chosenMemberOrgId: 0,
      chosenSectorId: 0,
      initialMemberOrgName: "",
      initialStatus: "",
      initialComment: "",
      initialSectorName: "",
    };
  },
  computed: {
    ...mapGetters("plans", ["getStudy"]),
    /**
     * Filter configuration for the table
     * Creates a switch to toggle waitlist visibility
     */
    filter() {
      return [
        {
          component: VSwitch,
          value: "status",
          staticClass: "mx-3 pa-0",
          default: false,
          attrs: {
            label: "Vis venteliste",
            inset: true,
            hideDetails: true,
          },
          apply: (value: any, model: any) => (!model && value != null) || model,
        },
      ];
    },
    /**
     * Table column configuration
     * Conditionally includes attendance percentage if course tracks hours
     */
    headers(props: any) {
      return [
        { text: "Rang.", value: "rank" },
        { text: "Handlinger", value: "actions", sortable: false },
        { text: "Navn", value: "userFullName" },
        { text: "Medlemsorganisasjon", value: "memberOrganization" },
        { text: "Sektor", value: "sector" },
        { text: "Status", value: "status" },
        { text: "Søknadsmetode", value: "appliedViaForm" },
        { text: "Bestiller", value: "purchaser.name" },
        { text: "Fakturamottaker", value: "invoiceRecipient.name" },
        { text: "Brukertype", value: "userTypeType" },
        { text: "Kommentar", value: "latestCourseParticipantComment" },
        props.course.plan.registerHours ? { text: "Oppmøteprosent", value: "currentAttendancePercentage" } : {},
        { text: "Oppdatert", value: "updated" },
      ];
    },
  },
  methods: {
    formatRelative,
    /**
     * Opens modal to add a new participant
     * Initializes modal state and sets appropriate headline
     */
    newParticipant() {
      this.$store.commit("courseParticipants/SET_PERSON", {});
      // Set all properties BEFORE opening modal
      this.modalHeadline = `Legg til ${this.getHeadLineType((this as any).getStudy.mainCourseId)}`;
      this.modalType = ModalType.Add;
      this.selectedUserId = 0;
      this.chosenMemberOrgId = 0;
      this.chosenSectorId = 0;
      // Reset pre-filled data when adding new participant
      this.initialMemberOrgName = "";
      this.initialStatus = "";
      this.initialComment = "";
      this.initialSectorName = "";

      this.showModal = true;
    },
    /**
     * Returns appropriate participant type label based on course type
     * @param value - mainCourseId to check
     * @returns 'student' for vocational schools, 'deltaker' for others
     */
    getHeadLineType(value: number) {
      return isVocationalSchool(value) ? "student" : "deltaker";
    },
    onePager(value: ApiGetCourseParticipantKursAdminDto) {
      this.$store.commit("courseParticipants/SET_PERSON", value);
      this.$router.push({
        path: `/kurset/${this.$route.params.id}/participants/${value.userId}`,
        query: {
          appliedViaForm: value.appliedViaForm.toString(),
          participantMemberOrgId: (value as any).memberOrganization?.id?.toString() || "",
        },
      });
    },
    editParticipant(value: any) {
      this.$store.commit("courseParticipants/SET_PERSON", value);
      // Set all properties BEFORE opening modal
      this.modalHeadline = `Rediger ${this.getHeadLineType((this as any).getStudy.mainCourseId)} (${
        value.userFullName
      })`;
      this.modalType = !this.isCourseClosed ? ModalType.Edit : ModalType.Display;
      this.selectedUserId = value.userId;
      this.chosenMemberOrgId = value.memberOrganization?.id ?? 0;
      this.chosenSectorId = value.sector?.id ?? 0;
      // Pre-fill data for immediate display
      this.initialMemberOrgName = value.memberOrganization?.name ?? "";
      this.initialStatus = value.status ?? "";
      this.initialComment = value.latestCourseParticipantComment?.comment ?? "";
      this.initialSectorName = value.sector?.type ?? "";
      this.showModal = true;
    },
  },
});
